import React, { useState } from 'react';
import { ArrowRight, Phone, Star, ChevronDown, CheckCircle2, AlertCircle } from 'lucide-react';
import { QuoteButton } from './QuoteModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Universal cover/hero section with form — inspired by wpcreative.com.au
 * Includes Google Premier Partner badge, service dropdown, description field, and polished form.
 * Form column is ~30% narrower than the text column so copy gets more room.
 */
const PageHero = ({
  eyebrow,
  title,
  titleItalic,
  titleSuffix,
  subtitle,
  description,
  ctaLabel = 'Get a FREE Quote',
  phone = '1800 006 296',
  showForm = true,
  formTitle = 'Get Your FREE Quote',
  formSubtext = 'One of our digital strategists will be in touch within 24 hours. Prefer to speak now? Call 1800 006 296.',
  ratingText = 'Trusted By 1000+ Australian Businesses',
  align = 'left',
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: (data.get('name') || '').toString().trim(),
      email: (data.get('email') || '').toString().trim(),
      phone: (data.get('phone') || '').toString().trim(),
      service: (data.get('service') || '').toString(),
      description: (data.get('description') || '').toString().trim(),
      source: 'page-hero',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
    };
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
      form.reset();
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError("Sorry — we couldn't send your request. Please call 1800 006 296 or try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-b from-[#f8fafc] via-white to-[#f1f5f9] pt-32 pb-20 md:pt-40 md:pb-28"
      data-testid="page-hero"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#15803d]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-16 h-[28rem] w-[28rem] rounded-full bg-[#db2777]/10 blur-3xl" />

      <div className="container relative z-10 mx-auto px-6 md:px-12">
        <div
          className={`grid ${
            showForm ? 'lg:grid-cols-[1.55fr_1fr]' : 'lg:grid-cols-1'
          } gap-12 lg:gap-14 items-center max-w-7xl mx-auto`}
        >
          <div className={align === 'center' && !showForm ? 'text-center mx-auto max-w-4xl' : ''}>
            {eyebrow && (
              <p className="inline-flex items-center gap-2 rounded-full bg-[#15803d]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-6">
                <span className="h-1.5 w-1.5 rounded-full bg-[#15803d]" />
                {eyebrow}
              </p>
            )}

            <h1 className="font-['Outfit'] text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-slate-900 leading-[1.05] mb-6">
              {title}
              {titleItalic && (
                <>
                  {' '}
                  <span className="italic font-serif text-[#15803d] font-medium">{titleItalic}</span>
                </>
              )}
              {titleSuffix && <> {titleSuffix}</>}
            </h1>

            {subtitle && (
              <p className="text-xl md:text-2xl font-semibold text-slate-700 mb-6 leading-relaxed">
                {subtitle}
              </p>
            )}

            {description && (
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl">
                {description}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <QuoteButton
                data-testid="page-hero-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#db2777] hover:bg-[#be185d] text-white px-8 py-4 font-semibold text-base md:text-lg shadow-lg shadow-[#db2777]/20 transition-all hover:shadow-xl hover:-translate-y-0.5"
              >
                {ctaLabel}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </QuoteButton>
              <a
                href={`tel:${phone.replace(/\s+/g, '')}`}
                data-testid="page-hero-phone"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 bg-white/70 backdrop-blur hover:border-[#15803d] hover:text-[#15803d] text-slate-800 px-8 py-4 font-semibold text-base md:text-lg transition-all"
              >
                <Phone size={18} /> {phone}
              </a>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="fill-[#fbbf24] text-[#fbbf24]" />
                ))}
              </div>
              <span className="text-sm font-semibold text-slate-600">{ratingText}</span>
            </div>
          </div>

          {showForm && (
            <div className="relative lg:max-w-sm w-full mx-auto lg:mx-0 lg:ml-auto">
              {/* Google Premier Partner badge - top-left */}
              <div className="absolute -top-5 -left-3 md:-top-6 md:-left-6 z-20">
                <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-2.5 md:p-3 flex items-center gap-2 hover:scale-105 transition-transform">
                  <img
                    src="/assets/google-partner-badge.png"
                    alt="Google Premier Partner"
                    className="h-10 md:h-12 w-10 md:w-12 object-contain"
                    loading="eager"
                  />
                  <div className="pr-1">
                    <p className="text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-slate-500 leading-tight">
                      Google
                    </p>
                    <p className="text-[11px] md:text-xs font-bold text-slate-900 leading-tight">
                      Premier Partner
                    </p>
                  </div>
                </div>
              </div>

              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#15803d]/25 via-transparent to-[#db2777]/25 blur-2xl" />
              <div className="relative rounded-3xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/10 overflow-hidden">
                {/* Gradient header */}
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 pt-10 pb-6 md:px-7 md:pt-12 md:pb-7 relative overflow-hidden">
                  <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-[#15803d]/30 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-[#db2777]/30 blur-3xl" />
                  <div className="relative">
                    <p className="inline-flex items-center gap-1.5 rounded-full bg-[#15803d] text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                      FREE Quote
                    </p>
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-1.5 leading-tight">
                      {formTitle}
                    </h2>
                    <p className="text-xs text-slate-300">
                      No lock-in contracts. 100% Aussie team.
                    </p>
                  </div>
                </div>

                {/* Form body */}
                <div className="p-6 md:p-7">
                  {submitted ? (
                    <div className="py-8 text-center" data-testid="hero-form-success">
                      <div className="mx-auto w-14 h-14 rounded-full bg-[#15803d]/10 flex items-center justify-center text-[#15803d] mb-4">
                        <CheckCircle2 size={28} />
                      </div>
                      <h4 className="text-lg font-bold text-slate-900 mb-2">Thanks!</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        We'll be in touch shortly.
                      </p>
                    </div>
                  ) : (
                    <form className="space-y-3" onSubmit={handleSubmit}>
                      <div>
                        <label className="block text-[10.5px] font-semibold text-slate-700 mb-1 uppercase tracking-wider">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="John Smith"
                          data-testid="hero-form-name"
                          className="w-full rounded-lg border-2 border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-[10.5px] font-semibold text-slate-700 mb-1 uppercase tracking-wider">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="you@company.com"
                          data-testid="hero-form-email"
                          className="w-full rounded-lg border-2 border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-[10.5px] font-semibold text-slate-700 mb-1 uppercase tracking-wider">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="04xx xxx xxx"
                          data-testid="hero-form-phone"
                          className="w-full rounded-lg border-2 border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label htmlFor="hero-form-service" className="block text-[10.5px] font-semibold text-slate-700 mb-1 uppercase tracking-wider">
                          Service *
                        </label>
                        <div className="relative">
                          <select
                            id="hero-form-service"
                            aria-label="Service of interest"
                            required
                            name="service"
                            defaultValue=""
                            data-testid="hero-form-service"
                            className="w-full appearance-none rounded-lg border-2 border-slate-200 bg-slate-50/50 px-3.5 py-2.5 pr-9 text-sm text-slate-900 focus:border-[#166534] focus:bg-white focus:ring-2 focus:ring-[#166534]/20 focus:outline-none transition-all cursor-pointer"
                          >
                            <option value="" disabled>Select a service...</option>
                            <option value="seo">SEO</option>
                            <option value="google-ads">Google Ads</option>
                            <option value="web-design">Web Design</option>
                            <option value="link-building">Link Building</option>
                            <option value="content-marketing">Content Marketing</option>
                            <option value="local-seo">Local SEO</option>
                            <option value="ecommerce-seo">eCommerce SEO</option>
                            <option value="google-penalty-removal">Google Penalty Removal</option>
                            <option value="other">Other</option>
                          </select>
                          <ChevronDown
                            size={16}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10.5px] font-semibold text-slate-700 mb-1 uppercase tracking-wider">
                          Tell us about your project
                        </label>
                        <textarea
                          rows={3}
                          name="description"
                          placeholder="Goals, target keywords, competitors..."
                          data-testid="hero-form-description"
                          className="w-full rounded-lg border-2 border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-[#15803d] focus:bg-white focus:ring-2 focus:ring-[#15803d]/20 focus:outline-none transition-all resize-none"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="hero-form-submit"
                        className="group w-full mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#db2777] to-[#be185d] hover:from-[#be185d] hover:to-[#9f1650] disabled:opacity-70 disabled:cursor-not-allowed text-white py-3 font-bold text-sm tracking-wide shadow-lg shadow-[#db2777]/30 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                      >
                        {submitting ? (
                          <>
                            <span className="h-3.5 w-3.5 rounded-full border-2 border-white/50 border-t-white animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            GET MY FREE QUOTE
                            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                          </>
                        )}
                      </button>

                      {error && (
                        <p
                          role="alert"
                          data-testid="hero-form-error"
                          className="flex items-start gap-2 text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5"
                        >
                          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                          <span>{error}</span>
                        </p>
                      )}

                      <p className="text-[10px] text-slate-500 text-center leading-relaxed pt-1">
                        {formSubtext}
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
